import { Link } from "react-router-dom";
import { GraduationCap, Landmark, ArrowRight } from "lucide-react";
import { Reveal, Eyebrow } from "../components/Reveal";
import { EnquiryModal } from "../components/EnquiryModal";
import { useTitle } from "../hooks/useTitle";

export default function About() {
  useTitle("About Us");
  return (
    <div data-testid="about-page">
      <section className="bg-[#0A192F] relative overflow-hidden py-16 lg:py-24">
        <div className="absolute inset-0 hero-grid" />
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <Eyebrow light>About SterlingNest</Eyebrow>
          <h1 className="font-display font-extrabold tracking-tight text-white text-3xl sm:text-4xl lg:text-5xl mt-4 max-w-3xl leading-[1.1]">
            We turn accounting knowledge into practical, job-ready capability.
          </h1>
          <p className="mt-6 text-slate-300 text-base max-w-2xl leading-relaxed">
            SterlingNest is first and foremost a practical accounting & taxation training institute. Our belief is
            simple: nobody becomes an accountant by watching slides. You become one by creating companies, passing
            vouchers, computing GST, deducting TDS and defending a balance sheet.
          </p>
        </div>
      </section>

      <section className="py-16 lg:py-24 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="grid md:grid-cols-2 gap-6">
          <Reveal>
            <div className="h-full bg-white rounded-2xl border border-slate-200 p-8" data-testid="about-training-card">
              <div className="w-12 h-12 rounded-xl bg-[#E8F5E9] flex items-center justify-center">
                <GraduationCap size={22} className="text-[#0F5132]" />
              </div>
              <h2 className="font-display font-extrabold text-xl text-[#0A192F] mt-5">Training Institute</h2>
              <p className="text-xs font-mono-sn font-bold text-[#0F5132] tracking-widest mt-1">OUR PRIMARY FOCUS</p>
              <p className="text-sm text-[#486581] leading-relaxed mt-4">
                Practical, hands-on programs in Tally Prime, GST and TDS — built around simulated real businesses,
                assignments and a signature 10-step capstone project. Our goal is not course completion; it is
                capability you can demonstrate in an interview and on the job.
              </p>
              <Link to="/courses" className="mt-6 inline-flex items-center gap-1.5 text-sm font-bold text-[#0F5132] hover:gap-3 transition-all">
                Explore courses <ArrowRight size={15} />
              </Link>
            </div>
          </Reveal>
          <Reveal delay={0.1}>
            <div className="h-full bg-white rounded-2xl border border-slate-200 p-8" data-testid="about-services-card">
              <div className="w-12 h-12 rounded-xl bg-[#FDF8EA] flex items-center justify-center">
                <Landmark size={22} className="text-[#C59B27]" />
              </div>
              <h2 className="font-display font-extrabold text-xl text-[#0A192F] mt-5">Professional Services</h2>
              <p className="text-xs font-mono-sn font-bold text-[#C59B27] tracking-widest mt-1">OUR EXTENSION</p>
              <p className="text-sm text-[#486581] leading-relaxed mt-4">
                A professional financial services wing offering accounting, GST & taxation, audit support, mutual
                fund, loan and insurance assistance — the same rigour we teach, delivered as a service to businesses
                and individuals.
              </p>
              <Link to="/services" className="mt-6 inline-flex items-center gap-1.5 text-sm font-bold text-[#0F5132] hover:gap-3 transition-all">
                Explore services <ArrowRight size={15} />
              </Link>
            </div>
          </Reveal>
        </div>

        <Reveal className="mt-16">
          <div className="bg-[#0A192F] rounded-3xl p-8 sm:p-12 relative overflow-hidden" data-testid="about-philosophy">
            <div className="absolute inset-0 hero-grid" />
            <div className="relative max-w-2xl">
              <Eyebrow light>Our Philosophy</Eyebrow>
              <p className="font-display font-bold text-white text-xl sm:text-2xl leading-snug mt-4">
                "The homepage of every institute says 'we teach Tally'. We would rather say: we help you turn
                accounting knowledge into practical, job-ready capability — and then we prove it with a capstone."
              </p>
              <div className="mt-8">
                <EnquiryModal
                  source="about-page"
                  trigger={
                    <button data-testid="about-enquire-btn" className="px-7 py-3.5 rounded-full bg-[#C59B27] text-[#0A192F] font-display font-bold text-sm hover:bg-[#d8ae3a] transition-colors">
                      Talk to a Counsellor
                    </button>
                  }
                />
              </div>
            </div>
          </div>
        </Reveal>
      </section>
    </div>
  );
}
